// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Blank for Interactive Game
//
// Include description of object & useful info here, please!
import InteractivePrototype from './interactivePrototype.ts';
import SkillCalculator from '../uTheory.Skills/skillCalculator.ts';
import * as Notation from '../notation.ts';
import utils from '../utils.ts';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import config from '../config.ts';
// import Note from '../uTheory.Notation/note';

class ScaleWrite extends InteractivePrototype {
  // Public constructor
  constructor(displayDiv, options) {
    super();
    this.targetSpeedFromSkills = ScaleWrite.registry
      .map((r) => r.skills)
      .reduce((a, b) => a.concat(b));
    this.settings = {
      instructions: {
        ariaLiveRegion: 0,
        ariaLive: 'assertive',
      },
      editor: {
        fixedWidth: 450,
        maxMeasurePadding: 450,
        disableTextInput: true,
        ariaTitle: {
          keySignature: false,
          barline: false,
          timeSignature: false,
          rhythm: false,
        },
        ariaLive: 'polite',
      },
      letters: {
        letterButtons: false,
        accidentalButtons: { noDoubleTap: true },
        checkButton: 'Check',
      },
      piano: false,
      checkMark: { rightAnswerLength: 1.2 },
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.layout = {
      editor: { percent: 95 },
      instructions: { maxPercent: 30 },
    };
    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    if (options.testMode) {
      this.settings.letters.checkButton = 'Submit';
    }
    // now call the prototype's init function:
    super.init(displayDiv, options).then(() => {
      this.priorScaleDegrees = [];
      this.wrongAnswerMode = 0;
    });
  }

  generateQuestions() {
    // In non drill mode, specify questions as:
    //  keynote/mode/asc or dsc
    //  modes: Major, minor, harmonicMinor, melodicMinor
    if (this.options.testMode) return;

    if (this.optionsTrue('drill')) {
      this.randomQuestionGenerator();
    } else {
      this.processManualQuestions();
    }

    this.prepareQuestions();
  }

  // read in this.options.questions:
  processManualQuestions(questions) {
    questions = questions || this.options.questions;
    this.scales = questions.map((question) => {
      const parts = question.split('/');
      const mode = parts[1];
      const note = utils.isNumeric(parts[0])
        ? new Notation.KeySignature(parts[0], mode.includes('m') ? 'minor' : 'Major').keyNote
        : new Notation.Note(parts[0]);

      const direction = typeof parts[2] !== 'undefined' && parts[2] === 'dsc' ? -1 : 1;
      const scale = new Notation.Scale(note, mode, direction);
      if (typeof parts[3] !== 'undefined') {
        scale.clef = parts[3];
      }
      if (typeof parts[4] !== 'undefined') {
        scale._givenNote = Number(parts[4]);
      }
      return scale;
    }, this);
  }

  getQuestionText(scale) {
    if (this.options.notFromTonic) {
      scale._givenNote = scale._givenNote || Math.floor(Math.random() * 6) + 1;
      const direction = scale.direction === 1 ? 'ascending' : 'descending';
      const mode = scale.modeToString();
      const degreeName =
        this.options.degreeName === 'text'
          ? `its ${scale.getScaleDegreeName(scale._givenNote)}`
          : `scale degree <span class='accidentals'>${scale._givenNote}</span>`;
      const note = scale.getNoteAtScaleDegree(scale._givenNote);
      const noteName = note.toAccidentalFontShortString();
      return `the <strong>${direction} ${mode}</strong> scale with<br /><strong>${noteName}</strong> as <strong>${degreeName}</strong>.`;
    }

    if (this.options.displayKeySignature) {
      const direction = scale.direction === 1 ? 'ascending' : 'descending';
      const mode = scale.modeToString();
      return `this key's <strong>${direction} ${mode}</strong> scale.`;
    }
    const scaleText = scale.toAriaString({}, true);
    return `${utils.aOrAn(scaleText)} <strong>${scaleText}</strong> scale.`;
  }

  prepareQuestions() {
    this.questions = [];
    this.scales.forEach((scale) => {
      const text = this.getQuestionText(scale);
      // If we're in the version with key sig given, we don't save any skills!
      const skills = this.options.displayKeySignature ? [] : this.calculateSkills(scale.mode);
      // Randomize Clef:
      const clef = scale.clef || Math.random() > 0.5 ? 'treble' : 'bass';

      const questionObject = {
        scale,
        text: [text, this.questions.length + 1],
        skills,
        direction: scale.direction,
        clef,
      };

      if (scale._givenNote) questionObject.givenNote = scale._givenNote;

      this.questions.push(questionObject);
    }, this);
    // set up global variables:
    if (this.optionsTrue('questionPrompt')) {
      this.questionPrompt = this.options.questionPrompt;
    } else {
      this.questionPrompt = 'Draw {question}';
    }
    this.maxQuestions = this.questions.length;
    if (!this.options.testMode) {
      this.questionPrompt += `<br /><small>#{question} of ${this.maxQuestions}</small>`;
    }
    this.questionNumber = 0;
    this.scaleDegree = 0;
  }

  initializeMusic() {
    const clef = new Notation.Clef('treble');
    this.clef = clef;
    const staff = new Notation.Staff(clef);
    this.noteHolders = [];
    this.measure = new Notation.Measure();

    for (let i = 0; i < 8; i++) {
      const noteHolder = new Notation.NoteHolder('', 'w');
      noteHolder.ghostIfEmpty = true;
      noteHolder.rhythm = new Notation.Rhythm('w');
      const options = { pitch: true, accidental: true, deleteable: false, selectable: true };
      noteHolder.editable = new Notation.Editable(noteHolder, options);
      this.measure.push(noteHolder);
      this.noteHolders.push(noteHolder);
    }
    //        var measure = new Notation.Measure(this.noteHolders);
    //        this.measures.push(measure);

    const voice = new Notation.Voice([this.measure]);
    voice.setStaff(staff);
    const timeSig = new Notation.TimeSignature('4/4');
    timeSig.hidden = true;
    this.voice = voice;

    const keySig = new Notation.KeySignature('C', 'Major');
    this.music = new Notation.Music(staff, voice, keySig, timeSig);
  }

  newQuestion() {
    this.priorScaleDegrees = [];
    this.noteHolders.forEach((nh) => {
      nh.color = undefined;
    });
    this.letterPanel.clearNoteName();
    this.editor.deselectAll();

    this.question = this.questions[this.questionNumber];

    for (let i = 0; i < 8; i++) {
      if (this.questionNumber > 0 || this.options.testMode) {
        this.noteHolders[i].editable.eventHandler.setAccidental('');
      }
      this.noteHolders[i].remove(this.noteHolders[i].notes[0]);

      this.noteHolders[i].editable.selected = false;
    }

    if (this.options.displayKeySignature) {
      const key = this.question.scale.keySignature || 'C Major';
      this.music.keySignatures[0].set(`${key}`);
      this.noteHolders.forEach((nh) => {
        nh.editable.options.keySignature = this.music.keySignatures[0];
      });
    }

    this.clef.setName(this.question.clef);

    this.editor.renderer.render();

    super.newQuestion();
  }

  // ## Setup new checkpoint question:
  newCheckpointQuestion(question) {
    return this.loadingPromise.then(() => {
      this.questionNumber = 0;
      this.processManualQuestions([question]);
      this.prepareQuestions();
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  calculateSkills(mode) {
    if (typeof this.skillCalculator === 'undefined') {
      this.skillCalculator = new SkillCalculator();
    }

    const skills = [this.skillCalculator.getStaffScaleSkill(mode)];
    if (this.options.notFromTonic && this.options.degreeName === 'text') {
      skills.push('scale_degree_names');
    }
    return skills;
  }

  // or generate random questions:
  randomQuestionGenerator() {
    const generator = new QuestionGenerator();
    this.scales = generator.multipleSkillsGenerate(
      this.options.skills,
      this.options.maxQuestions || 5,
      this.options
    );
    // randomize direction:
    this.scales.forEach((scale) => {
      if (Math.random() > 0.5) {
        scale.setDirection(-1);
      }
    });
  }

  checkAnswer() {
    if (this.options.testMode) return this.checkpointAnswer();
    if (this.rightAnswerDelay) return undefined;

    const scale = this.questions[this.questionNumber].scale;

    /** @type {Note[]} */
    const priorNotes = this.priorScaleDegrees;

    /** @type {Note[]} */
    const answerNotes = this.noteHolders.filter((nh) => !nh.isEmpty()).map((nh) => nh.notes[0]);

    /** @type {Note[]} */
    const rightNotes = scale.direction === -1 ? scale.notes.slice(0).reverse() : scale.notes;

    const emptyNotes = rightNotes.length - answerNotes.filter((n) => n).length;
    if (emptyNotes !== 0) {
      this.checkMark.warn('Add all 8 notes first.');
      return undefined;
    }
    // From here on out, we can assume we have all the notes:

    const { rightAnswers, wrongAnswers } = rightNotes.reduce(
      (accum, rightNote, i) => {
        const answerNote = answerNotes[i];
        const priorNote = priorNotes[i];
        // if same as before, don't count it:
        if (priorNote && priorNote.equals(answerNote)) return accum;

        if (answerNote.equalsIgnoreOctave(rightNote)) {
          accum.rightAnswers++;
        } else {
          accum.wrongAnswers++;
        }
        return accum;
      },
      { rightAnswers: 0, wrongAnswers: 0 }
    );

    const allCorrect =
      !emptyNotes && rightNotes.every((n, i) => n.equalsIgnoreOctave(answerNotes[i]));

    // Keep cache of scale degrees:
    this.priorScaleDegrees = this.noteHolders
      .map((nh) => (nh.notes[0] ? nh.notes[0].clone() : false))
      .filter((n) => n);

    this.editor.deselectAll();
    this.playAnswer();
    if (allCorrect) {
      this.rightAnswer(rightAnswers, wrongAnswers);
    } else {
      this.wrongAnswer(rightAnswers, wrongAnswers);
    }
    return undefined;
  }

  wrongAnswer(rightAnswers, wrongAnswers) {
    this.wrongAnswerMode++;
    this.checkMark.x();
    this.trackAnswer(rightAnswers, wrongAnswers);
    this.colorWrongNotes();
  }

  rightAnswer(rightAnswers, wrongAnswers) {
    this.trackAnswer(rightAnswers, wrongAnswers);
    this.colorWrongNotes();
    this.wrongAnswerMode = 0;
    super.rightAnswer(1200, { noProgressTracker: true });
  }

  debugRightAnswer() {
    this.rightAnswer(8, 0);
  }

  debugWrongAnswer() {
    this.wrongAnswer(0, 8);
  }

  /**
   * Updates the progress tracker with number of correct and incorrect answers
   *
   * @param {number} rightAnswers number of correct answers
   * @param {number} wrongAnswers number of incorrect answers
   */
  trackAnswer(rightAnswers, wrongAnswers) {
    if (!rightAnswers && !wrongAnswers) return;
    const trackerObject = {
      skill: this.question.skills[0],
      questions: rightAnswers + wrongAnswers,
      right: rightAnswers,
      wrong: wrongAnswers,
    };
    this.progressTracker.multipleAnswers([trackerObject], rightAnswers + wrongAnswers, false);
  }

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv);
    this.editor.disableEditor();
    this.editor.renderer.render();
    super.gameOver();
  }

  colorWrongNotes() {
    if (this.wrongAnswerMode < 2) return;
    const scale = this.questions[this.questionNumber].scale;
    const scaleNotes = scale.direction === -1 ? scale.notes.concat([]).reverse() : scale.notes;

    this.noteHolders.forEach((nh, index) => {
      const scaleNote = scaleNotes[index];
      const studentNote = nh.notes[0];
      if (!scaleNote || !studentNote) return; // this should never happen.
      const correct = scaleNote.equalsIgnoreOctave(studentNote);
      if (correct) {
        delete nh.color;
      } else {
        nh.color = 'red';
      }
    });
    this.editor.renderer.render();
  }

  getAnswerAndData() {
    let weightedRightAnswers = 0;
    let weightedWrongAnswers = 0;
    const scaleNotes = this.questions[this.questionNumber].scale.notes;
    if (this.question.direction === -1) scaleNotes.reverse();
    const notes = this.noteHolders.map((nh) => nh.notes[0]);
    const veryLightRed = config.Colors.veryLightRed;
    const red = config.Colors.lightRed;

    const octaveDisplacement = notes[0] ? notes[0].octave - scaleNotes[0].octave : 0;

    // Clear any highlighted notes:
    this.editor.deselectAll();

    // Check and label wrong answers:
    scaleNotes.forEach((rightNote, index) => {
      // No answer? Very light red:
      if (typeof notes[index] === 'undefined') {
        rightNote.octave += octaveDisplacement;
        this.noteHolders[index].push(rightNote);
        this.noteHolders[index].color = veryLightRed;
        weightedWrongAnswers++;
        return;
      }
      // Wrong answer?
      if (!rightNote.equalsIgnoreOctave(notes[index])) {
        this.noteHolders[index].color = red;
        weightedWrongAnswers++;
        return;
      }
      // Right answer?
      weightedRightAnswers++;
    }, this);

    // Redraw it:
    this.editor.renderer.render();

    const notesToList = function (str, note, index) {
      const spacer = index ? '' : ' ';
      if (!note) return `${spacer}_`;
      return str + spacer + note.toShortString();
    };
    const studentAnswerText = notes.reduce(notesToList, '');
    const correctAnswerText = scaleNotes.reduce(notesToList, '');

    if (this.options.allOrNothingGrading) {
      weightedRightAnswers = !weightedWrongAnswers ? 8 : 0;
      weightedWrongAnswers = !weightedWrongAnswers ? 0 : 8;
    }

    return {
      question: this.instructionsDiv.innerHTML,
      questionType: 'ScaleWrite',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: null,
      studentAnswerText,
      studentAnswerSVG: this.editor.exportSVG(),
      correctAnswerText,
      rightAnswers: weightedRightAnswers,
      wrongAnswers: weightedWrongAnswers,
      skills: this.questions[this.questionNumber].skills,
    };
  }

  checkDirection() {
    if (
      this.noteHolders[2].isEmpty() &&
      !this.noteHolders[0].isEmpty() &&
      !this.noteHolders[1].isEmpty()
    ) {
      const shouldWriteAscending = this.question.direction === 1;
      const shouldWriteDescending = !shouldWriteAscending;
      const writingAscending = this.noteHolders[1].notes[0].isGreaterThanIgnoreAccidental(
        this.noteHolders[0].notes[0]
      );
      const writingDescending = this.noteHolders[1].notes[0].isLessThanIgnoreAccidental(
        this.noteHolders[0].notes[0]
      );

      if (shouldWriteAscending && writingDescending) {
        this.checkMark.warn('Careful: Write an ASCENDING scale.');
        this.noteHolders[1].remove(this.noteHolders[1].notes[0]);
      }
      if (shouldWriteDescending && writingAscending === true) {
        this.checkMark.warn('Careful: Write a DESCENDING scale.');
        this.noteHolders[1].remove(this.noteHolders[1].notes[0]);
      }
    }
  }

  playAnswer() {
    this.player.playMusic(this.music, { tempo: 800 });
  }

  addListeners() {
    this.letterPanel.addAccidentalCallback(this.accidental, this);
    this.letterPanel.addCheckCallback(this.checkAnswer, this);

    this.editor.addListener(
      'contactEventEnd',
      () => {
        this.letterPanel.clearNoteName();
        this.colorWrongNotes();
        this.checkDirection();
      },
      this
    );
    const directionArrow = () => {
      this.letterPanel.clearNoteName();
      this.colorWrongNotes();
    };

    this.editor.addListener('up', () => this.checkDirection());
    this.editor.addListener('down', () => this.checkDirection());
    this.editor.addListener('left', directionArrow);
    this.editor.addListener('right', directionArrow);
  }

  accidental(accidental) {
    this.editor.setAccidental(accidental);
    //      this.clearWarning();
  }
}

ScaleWrite.registry = [
  {
    description: 'Write Major Scales',
    skills: ['major_scales_staff'],
  },
  {
    description: 'Write minor scales',
    skills: ['natural_minor', 'harmonic_minor', 'melodic_minor'],
  },
];

export default ScaleWrite;
